import html2canvas from "html2canvas";

const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 10;
const BOTTOM_MARGIN = 10;
const CONTENT_HORIZONTAL_MARGIN = 10;
const MIN_SLICE_HEIGHT_PX = 24;
const CONTENT_CAPTURE_WIDTH = 1170;
const CONTENT_PDF_WIDTH = PAGE_WIDTH - 2 * CONTENT_HORIZONTAL_MARGIN;

function getElementBoundsRelativeTo(container, el, scale) {
  const containerRect = container.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const top = (elRect.top - containerRect.top + container.scrollTop) * scale;
  const height = elRect.height * scale;
  return { top: Math.round(top), bottom: Math.round(top + height) };
}

function getActualContentHeight(element) {
  const containerRect = element.getBoundingClientRect();
  let maxBottom = 0;

  element.querySelectorAll("*").forEach((child) => {
    const childRect = child.getBoundingClientRect();
    const relativeBottom = childRect.bottom - containerRect.top;
    if (relativeBottom > maxBottom) {
      maxBottom = relativeBottom;
    }
  });

  return Math.max(Math.ceil(maxBottom), 1);
}

function getAvoidBreakRegions(element, scale) {
  const regions = [];
  const selectors = "tr, tbody.pdf-avoid-break, .pdf-avoid-break";
  element.querySelectorAll(selectors).forEach((el) => {
    const { top, bottom } = getElementBoundsRelativeTo(element, el, scale);
    if (bottom > top) {
      regions.push({ top, bottom });
    }
  });
  return regions.sort((a, b) => a.top - b.top);
}

function getSafeSliceStart(yStart, regions) {
  for (const { top, bottom } of regions) {
    if (yStart > top && yStart < bottom) {
      return bottom;
    }
  }
  return yStart;
}

function getSafeSliceEnd(yStart, maxSliceEnd, maxSliceHeightPx, regions) {
  let sliceEnd = maxSliceEnd;

  for (const region of regions) {
    if (region.top < yStart && region.bottom > yStart && region.bottom <= maxSliceEnd) {
      sliceEnd = Math.max(sliceEnd, region.bottom);
    }
  }

  for (const { top, bottom } of regions) {
    if (top < yStart) {
      continue;
    }
    if (top >= sliceEnd) {
      break;
    }

    const regionHeight = bottom - top;
    if (sliceEnd < bottom && regionHeight <= maxSliceHeightPx) {
      sliceEnd = Math.min(sliceEnd, top);
    }
  }

  if (sliceEnd <= yStart) {
    sliceEnd = maxSliceEnd;
  }

  const sliceHeight = sliceEnd - yStart;
  if (sliceHeight < MIN_SLICE_HEIGHT_PX && maxSliceEnd - yStart > MIN_SLICE_HEIGHT_PX) {
    for (const { top } of regions) {
      if (top > yStart && top <= maxSliceEnd) {
        return top;
      }
    }
    return maxSliceEnd;
  }

  return sliceEnd;
}

function getSliceHorizontalBounds(canvas, yStart, sliceHeight) {
  const ctx = canvas.getContext("2d");
  const { width } = canvas;
  const imageData = ctx.getImageData(0, yStart, width, sliceHeight);
  const { data } = imageData;

  let left = width;
  let right = 0;

  for (let y = 0; y < sliceHeight; y += 2) {
    for (let x = 0; x < width; x += 2) {
      const i = (y * width + x) * 4;
      const alpha = data[i + 3];
      if (alpha === 0) {
        continue;
      }

      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      if (r > 252 && g > 252 && b > 252) {
        continue;
      }

      if (x < left) {
        left = x;
      }
      if (x > right) {
        right = x;
      }
    }
  }

  if (left >= right) {
    return { left: 0, width };
  }

  const padding = Math.max(4, Math.round(width * 0.01));
  left = Math.max(0, left - padding);
  right = Math.min(width - 1, right + padding);

  return { left, width: right - left + 1 };
}

function applyCaptureLayout(element, styles) {
  const saved = {
    width: element.style.width,
    maxWidth: element.style.maxWidth,
    paddingTop: element.style.paddingTop,
    paddingBottom: element.style.paddingBottom,
    paddingLeft: element.style.paddingLeft,
    paddingRight: element.style.paddingRight,
    marginBottom: element.style.marginBottom,
    marginLeft: element.style.marginLeft,
    marginRight: element.style.marginRight,
    boxSizing: element.style.boxSizing,
    hadDesktop: element.classList.contains(styles.desktop),
    hadForceDesktop: element.classList.contains(styles.forceDesktop),
    hadPdfCapture: styles.pdfCapture
      ? element.classList.contains(styles.pdfCapture)
      : false,
    parent: element.parentNode,
    nextSibling: element.nextSibling,
  };

  const captureHost = document.createElement("div");
  captureHost.setAttribute("data-pdf-capture-host", "true");
  captureHost.style.cssText = [
    "position: fixed",
    "left: -12000px",
    "top: 0",
    `width: ${CONTENT_CAPTURE_WIDTH}px`,
    "background: #ffffff",
    "overflow: visible",
    "pointer-events: none",
  ].join(";");

  document.body.appendChild(captureHost);
  captureHost.appendChild(element);

  element.classList.add(styles.desktop);
  element.classList.add(styles.forceDesktop);
  if (styles.pdfCapture) {
    element.classList.add(styles.pdfCapture);
  }
  element.style.width = "100%";
  element.style.maxWidth = "100%";
  element.style.boxSizing = "border-box";
  element.style.paddingTop = "0";
  element.style.paddingBottom = "0";
  element.style.paddingLeft = "15px";
  element.style.paddingRight = "15px";
  element.style.marginBottom = "0";
  element.style.marginLeft = "0";
  element.style.marginRight = "0";

  return () => {
    if (saved.nextSibling) {
      saved.parent.insertBefore(element, saved.nextSibling);
    } else {
      saved.parent.appendChild(element);
    }
    captureHost.remove();

    element.style.width = saved.width;
    element.style.maxWidth = saved.maxWidth;
    element.style.paddingTop = saved.paddingTop;
    element.style.paddingBottom = saved.paddingBottom;
    element.style.paddingLeft = saved.paddingLeft;
    element.style.paddingRight = saved.paddingRight;
    element.style.marginBottom = saved.marginBottom;
    element.style.marginLeft = saved.marginLeft;
    element.style.marginRight = saved.marginRight;
    element.style.boxSizing = saved.boxSizing;
    if (!saved.hadDesktop) {
      element.classList.remove(styles.desktop);
    }
    if (!saved.hadForceDesktop) {
      element.classList.remove(styles.forceDesktop);
    }
    if (styles.pdfCapture && !saved.hadPdfCapture) {
      element.classList.remove(styles.pdfCapture);
    }
  };
}

export async function captureRoleReviewHeaderFooter({
  headerElement,
  footerElement,
  forceHFClass,
  scale = 2,
}) {
  headerElement.classList.add(forceHFClass);
  const roleReviewTitle = headerElement.querySelector("#RolerevirTitle");
  const roleReviewContent = headerElement.querySelector("#RolerevirContent");

  const originalTitleFontSize = roleReviewTitle?.style.fontSize;
  const originalContentFontSize = roleReviewContent?.style.fontSize;
  if (roleReviewTitle) roleReviewTitle.style.fontSize = "55px";
  if (roleReviewContent) roleReviewContent.style.fontSize = "35px";

  const headerCanvas = await html2canvas(headerElement, {
    scale,
    useCORS: true,
  });
  const headerImgData = headerCanvas.toDataURL("image/jpeg", 0.6);
  const headerImgHeight =
    (headerCanvas.height * PAGE_WIDTH) / headerCanvas.width;

  if (roleReviewTitle) roleReviewTitle.style.fontSize = originalTitleFontSize;
  if (roleReviewContent) roleReviewContent.style.fontSize = originalContentFontSize;

  footerElement.classList.add(forceHFClass);
  const footerTitle = footerElement.querySelector("#FooterTitle");
  const originalFooterTitleSize = footerTitle?.style.fontSize;
  if (footerTitle) {
    footerTitle.style.marginBottom = "55px";
    footerTitle.style.fontSize = "46px";
  }

  footerElement.querySelectorAll("a").forEach((a) => {
    a.style.display = "none";
  });
  footerElement
    .querySelectorAll("#forMoreInfo, #contactOrVisit")
    .forEach((span) => {
      span.style.display = "none";
    });

  const footerCanvas = await html2canvas(footerElement, {
    scale,
    useCORS: true,
  });
  const footerImgData = footerCanvas.toDataURL("image/jpeg", 0.6);
  const footerImgHeight =
    (footerCanvas.height * PAGE_WIDTH) / footerCanvas.width;

  if (footerTitle) {
    footerTitle.style.marginBottom = "";
    footerTitle.style.fontSize = originalFooterTitleSize;
  }
  footerElement.querySelectorAll("a").forEach((a) => {
    a.style.display = "";
  });
  footerElement
    .querySelectorAll("#forMoreInfo, #contactOrVisit")
    .forEach((span) => {
      span.style.display = "";
    });

  return {
    headerImgData,
    headerImgHeight,
    footerImgData,
    footerImgHeight,
  };
}

export function addRoleReviewHeaderAndFooter(
  pdf,
  { headerImgData, headerImgHeight, footerImgData, footerImgHeight }
) {
  pdf.addImage(headerImgData, "jpeg", 0, 0, PAGE_WIDTH, headerImgHeight);
  pdf.addImage(
    footerImgData,
    "jpeg",
    0,
    PAGE_HEIGHT - footerImgHeight,
    PAGE_WIDTH,
    footerImgHeight
  );
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(13);
  const yText = PAGE_HEIGHT - 6;
  pdf.text("For more information, contact", 13, yText);
  pdf.textWithLink("info@activatehcg.com", 75, yText, {
    url: "mailto:info@activatehcg.com",
  });
  pdf.text("or visit our website", 122, yText);
  pdf.textWithLink("ActivateHCG.com", 162, yText, {
    url: "https://www.activatehcg.com",
  });
}

export async function captureAndAddRoleReviewContent(pdf, {
  element,
  headerAssets,
  styles,
  scale = 2,
}) {
  const { headerImgHeight, footerImgHeight } = headerAssets;
  const availableHeight =
    PAGE_HEIGHT - headerImgHeight - footerImgHeight - MARGIN - BOTTOM_MARGIN;

  const restoreCaptureLayout = applyCaptureLayout(element, styles);

  const captureHeight = getActualContentHeight(element);

  const contentCanvas = await html2canvas(element, {
    width: CONTENT_CAPTURE_WIDTH,
    height: captureHeight,
    scale,
    useCORS: true,
  });

  const avoidBreakRegions = getAvoidBreakRegions(element, scale);
  restoreCaptureLayout();

  const canvasSlice = document.createElement("canvas");
  canvasSlice.width = contentCanvas.width;
  const pixelRatio = contentCanvas.width / CONTENT_PDF_WIDTH;
  const sliceHeightPx = availableHeight * pixelRatio;

  let yPositionPx = 0;
  let pageCount = 0;

  while (yPositionPx < contentCanvas.height) {
    yPositionPx = getSafeSliceStart(yPositionPx, avoidBreakRegions);
    if (yPositionPx >= contentCanvas.height) {
      break;
    }

    const remainingHeight = contentCanvas.height - yPositionPx;
    const maxSliceEnd = yPositionPx + Math.min(sliceHeightPx, remainingHeight);
    const safeSliceEnd = getSafeSliceEnd(
      yPositionPx,
      maxSliceEnd,
      sliceHeightPx,
      avoidBreakRegions
    );
    const currentSliceHeight = Math.max(
      1,
      Math.min(safeSliceEnd - yPositionPx, remainingHeight)
    );

    canvasSlice.width = contentCanvas.width;
    canvasSlice.height = currentSliceHeight;
    const ctx = canvasSlice.getContext("2d");
    ctx.clearRect(0, 0, canvasSlice.width, canvasSlice.height);
    ctx.drawImage(
      contentCanvas,
      0,
      yPositionPx,
      contentCanvas.width,
      currentSliceHeight,
      0,
      0,
      contentCanvas.width,
      currentSliceHeight
    );

    const sliceData = canvasSlice.toDataURL("image/jpeg");
    const croppedPdfWidth = CONTENT_PDF_WIDTH;
    const slicePdfHeight =
      (currentSliceHeight * CONTENT_PDF_WIDTH) / contentCanvas.width;
    const xOffset = CONTENT_HORIZONTAL_MARGIN;

    if (pageCount === 0) {
      addRoleReviewHeaderAndFooter(pdf, headerAssets);
      pdf.addImage(
        sliceData,
        "jpeg",
        xOffset,
        headerImgHeight + MARGIN,
        croppedPdfWidth,
        slicePdfHeight
      );
    } else {
      pdf.addPage();
      addRoleReviewHeaderAndFooter(pdf, headerAssets);
      pdf.addImage(
        sliceData,
        "jpeg",
        xOffset,
        headerImgHeight + MARGIN,
        croppedPdfWidth,
        slicePdfHeight
      );
    }

    yPositionPx += currentSliceHeight;
    pageCount += 1;
  }

  return pageCount;
}

export { PAGE_WIDTH, PAGE_HEIGHT, CONTENT_PDF_WIDTH, CONTENT_HORIZONTAL_MARGIN };
